import { workouts } from '../../services/api';

export type StructuredFitSportType = 'CYCLING' | 'RUNNING';

/** Map workout type id → Garmin FIT sport (matches Ai-Tempo). */
export function resolveStructuredFitSportType(workoutTypeId?: number): StructuredFitSportType {
  return workoutTypeId === 1 ? 'RUNNING' : 'CYCLING';
}

export function base64ToBlob(base64: string, mime = 'application/octet-stream'): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export function buildStructuredFitFilename(title: string | undefined, workoutId: number): string {
  const safe = String(title || `workout-${workoutId}`)
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `${safe || `workout-${workoutId}`}.fit`;
}

export async function fetchStructuredFitFile(
  workoutId: number,
  sportType?: StructuredFitSportType,
): Promise<{ filename: string; blob: Blob }> {
  const { filename, base64 } = await workouts.downloadStructuredFit(workoutId, { sportType });
  return { filename, blob: base64ToBlob(base64) };
}

/** Trigger browser download of a .fit file. */
export function triggerFitFileDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Share FIT via native sheet (Garmin Connect, Files, …).
 * Returns true when share succeeded; false when Web Share is unavailable.
 */
export async function shareFitFileToDevice(
  blob: Blob,
  filename: string,
  title?: string,
): Promise<boolean> {
  const file = new File([blob], filename, { type: 'application/octet-stream' });
  if (!navigator.share || !navigator.canShare?.({ files: [file] })) {
    return false;
  }
  await navigator.share({
    files: [file],
    title: title ?? filename,
  });
  return true;
}
